import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Dashboard from './dashboard/Dashboard';
import Layout from './Layout';
import Login from './dashboard/Login';
import Register from './dashboard/Signup';
import Settings from './account/Settings';
import Campaigns from './dashboard/Campaigns';
import Campaign from './dashboard/Campaign';
import Examples from './dashboard/Examples';
import Play from './games/Play';
import Editor from './games/Editor';
import Wizard from './games/Wizard';
import { UserProvider, useUser } from './userContext';
import { CreditModalProvider } from './components/CreditModalContext';
import { ClientProvider } from './clientContext';
import { FontProvider } from './contexts/FontContext';
import PrivateRoute from './PrivateRoute';
import AdminRoute from './components/AdminRoute';
import AdminDashboard from './admin/AdminDashboard';
import SharedByUsers from './dashboard/SharedByUsers';
import ScrollToTop from './dashboard/ScrollToTop';
import PlanModal from './dashboard/PlanModal';
import Error from './account/ErrorPage';
import NewCampaign from './campaign/CreateCampaign';
import DefaultLoading from './common/DefaultLoading';
import PublicGameCreator from './games/PublicGameCreator';
import LeaderboardPage from './pages/LeaderboardPage';
import CampaignResultsPage from './pages/CampaignResultsPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import AccountSetup from './pages/AccountSetup';
import { initializeDynamicSEO } from './utils/seo';
import CookieConsent from './components/CookieConsent';
import ErrorBoundary from './components/ErrorBoundary';
import { trackPageView } from './utils/analytics';
import Landing from './landing/Landing';
import PricingPage from './pages/PricingPage';
import BlogExample from './pages/BlogExample';

const AppContent = () => {
  const { loading, getStudioRedirectPath } = useUser();
  const location = useLocation();

  // Initialize dynamic SEO (favicon, title) on app load
  React.useEffect(() => {
    initializeDynamicSEO();
  }, []);

  // Track SPA page views after consent
  React.useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`, document.title);
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    const handleConsentUpdate = (e) => {
      if (e.detail?.analytics) {
        trackPageView(`${location.pathname}${location.search}`, document.title);
      }
    };
    window.addEventListener('cookieConsentUpdate', handleConsentUpdate);
    return () => window.removeEventListener('cookieConsentUpdate', handleConsentUpdate);
  }, [location.pathname, location.search]);

  // Root redirect
  const RootRedirect = () => {
    if (loading) return <DefaultLoading />;
    const target = getStudioRedirectPath();
    if (!target) return <DefaultLoading />;
    return <Navigate to={target} replace />;
  };

  const StudioRoute = ({ children }) => {
    return <PrivateRoute>{children}</PrivateRoute>;
  };

  const BuilderRoute = ({ children }) => {
    return <PrivateRoute>{children}</PrivateRoute>;
  };

  const isLanding = location.pathname === '/';
  // /leaderboard/ is a kiosk-style fixed-viewport display (fullscreen toggle,
  // QR sidebar, auto-scrolling ticker). It self-manages overflow internally,
  // so it must NOT be in the scrollable list — otherwise the outer wrapper
  // grows to the widest child and the QR sidebar gets clipped off the side.
  const isPublicScrollable = isLanding || location.pathname.startsWith('/results/') || location.pathname.startsWith('/privacy') || location.pathname.startsWith('/terms') || location.pathname.startsWith('/blog');
  return (
    <div className={`flex flex-col flex-1 ${isPublicScrollable ? 'min-h-screen overflow-auto' : 'min-h-0 overflow-hidden'}`}>
      <ScrollToTop />
      <CookieConsent />
      <Routes>
        {/* Marketing landing at root */}
        <Route path="/" element={<Landing />} />
        <Route path="admin" element={<Login />} />
        <Route path="login" element={<Navigate to="/studio/login" replace />} />
        <Route path="signin" element={<Navigate to="/studio/login" replace />} />
        <Route path="signup" element={<Navigate to="/studio/register" replace />} />
        <Route path="register" element={<Navigate to="/studio/register" replace />} />
        <Route path="pricing" element={<PricingPage />} />
        
        {/* Public play routes */}
        <Route path="p/:id" element={<Play />} />
        <Route path="play/:id" element={<Play />} />
        <Route path="g/:id" element={<Play />} />
        <Route path="leaderboard/:id" element={<LeaderboardPage />} />
        <Route path="results/:id" element={<CampaignResultsPage />} />

        {/* Public content pages */}
        <Route path="blog/embed-a-game" element={<BlogExample />} />

        {/* Public legal pages */}
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="terms" element={<TermsOfService />} />
        
        {/* Account setup - top-level so it always matches, no nesting issues */}
        <Route path="studio/setup" element={<AccountSetup />} />
        
        {/* Studio routes */}
        <Route path="studio">
          {/* Redirect studio landing to login/dashboard based on auth */}
          <Route index element={<RootRedirect />} />
          
          {/* Studio auth routes */}
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          
          {/* Protected studio business routes - Layout as wrapper */}
          <Route path="*" element={<Layout />}>
            <Route path="dashboard" element={<StudioRoute><Dashboard /></StudioRoute>} />
            <Route path="plan" element={<StudioRoute><PlanModal /></StudioRoute>} />
            <Route path="support" element={<Navigate to="/studio/settings/help" replace />} />
            <Route path="settings/*" element={<StudioRoute><Settings /></StudioRoute>} />
            <Route path="success" element={<StudioRoute><Dashboard /></StudioRoute>} />
            <Route path="campaigns" element={<StudioRoute><Campaigns /></StudioRoute>} />
            <Route path="campaign/:id" element={<StudioRoute><Campaign /></StudioRoute>} />
            <Route path="games" element={<StudioRoute><Examples /></StudioRoute>} />
            <Route path="templates" element={<Navigate to="/studio/games" replace />} />
            <Route path="shared" element={<StudioRoute><SharedByUsers /></StudioRoute>} />
            <Route path="new" element={<StudioRoute><NewCampaign /></StudioRoute>} />
            <Route path="admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          </Route>
        </Route>

        {/* Editor route - outside of Layout */}
        <Route
          path="editor/:id"
          element={
            <BuilderRoute>
              <Editor />
            </BuilderRoute>
          }
        />

        {/* New-game wizard - outside of Layout, full-screen.
            id === 'new' = pre-creation (game type picker is step 1). */}
        <Route
          path="wizard/:id"
          element={
            <BuilderRoute>
              <Wizard />
            </BuilderRoute>
          }
        />

        {/* Other routes */}
        <Route path="error" element={<Error />} />

        {/* Catch all route - redirect to login */}
        <Route path="*" element={<Navigate to="/studio/login" replace />} />
      </Routes>
    </div>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <FontProvider>
          <UserProvider>
            <ClientProvider>
              <CreditModalProvider>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
              </CreditModalProvider>
            </ClientProvider>
          </UserProvider>
        </FontProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
};

export default App;
